import React, { useState, useEffect, useRef } from 'react';
import useAuthStore from '../store/authStore';
import { useDashboard } from '../store/useDashboard';
import { filialeService } from '../services/api';
import api from '../services/api';
import useEstMobile from '../hooks/useEstMobile';

import { messageSeuil, seuilDeFiliale } from '../constants/seuilPdg';
import {
  IconeListe, IconeNote, IconeAvion, IconeRecu, IconeRafraichir, IconeDocument,
  IconeTrombone, IconeDossier, IconeFeu, IconeEpingle, IconeHorloge, IconeImage,
  IconeOeil, IconeAlerte, IconeValide, IconeFermer,
} from '../components/IconesSVG';

const TYPES_DOCS = [
  {
    key: 'expression_besoin',
    label: 'Fiche Expression de Besoin',
    icon: IconeListe,
    color: '#2980B9',
    bg: '#EAF4FB',
    desc: 'Demande d achat de materiel ou service',
        extensions: ['pdf', 'jpg', 'jpeg', 'png', 'docx'],
  },
  {
    key: 'bon_commande',
    label: 'Bon de Commande',
    icon: IconeNote,
    color: '#8E44AD',
    bg: '#F5EEF8',
    desc: 'Ordre d achat a un fournisseur',
        extensions: ['pdf', 'docx', 'xlsx'],
  },
  {
    key: 'frais_mission',
    label: 'Note de Frais de Mission',
    icon: IconeAvion,
    color: '#E67E22',
    bg: '#FDF2E9',
    desc: 'Remboursement de frais engages en mission',
        extensions: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    key: 'facture',
    label: 'Facture Fournisseur',
    icon: IconeRecu,
    color: '#16A085',
    bg: '#E8F8F5',
    desc: 'Facture a approuver et payer',
        extensions: ['pdf', 'jpg', 'jpeg'],
  },
  {
    key: 'regularisation',
    label: 'Regularisation',
    icon: IconeRafraichir,
    color: '#935116',
    bg: '#FDF2E9',
    desc: 'Regularisation d un achat deja effectue',
        extensions: ['pdf', 'jpg', 'jpeg', 'png', 'docx'],
  },
];

const CATEGORIES_FICHIER = [
  { key: 'document_principal', label: 'Document principal' },
  { key: 'devis', label: 'Devis fournisseur' },
  { key: 'facture', label: 'Facture' },
  { key: 'justificatif', label: 'Justificatif' },
  { key: 'autre', label: 'Autre' },
];

export default function NouveauDocumentPage() {
  const estMobile = useEstMobile();
  const { allerDashboard } = useDashboard();
  const { user } = useAuthStore();
  const [step, setStep] = useState(1); // Etape Type&Document masquee pour l'instant, on demarre direct sur Informations
  const [typeSelectionne, setTypeSelectionne] = useState(TYPES_DOCS[0]); // Type par defaut pre-selectionne (etape masquee)
  const [circuitsCharges, setCircuitsCharges] = useState({});
  const [fichiers, setFichiers] = useState([]);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [filiales, setFiliales] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    titre: '',
    montant: '',
    filiale: '',
    ville: '',
    description: '',
    priorite: 'P2',
  });

  const chargerCircuit = async (typeKey) => {
    if (circuitsCharges[typeKey]) return circuitsCharges[typeKey];
    try {
      const res = await api.get('/demandes/circuit/' + typeKey);
      const etapes = res.data || [];
      const texte = etapes
        .filter(e => e.role)
        .map(e => e.label)
        .join(' → ');
      setCircuitsCharges(prev => ({ ...prev, [typeKey]: texte }));
      return texte;
    } catch (err) {
      return 'Circuit indisponible';
    }
  };

  useEffect(() => {
    TYPES_DOCS.forEach(t => chargerCircuit(t.key));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- effet volontairement limite au montage
  }, []);

  useEffect(() => {
    filialeService.getAll().then(res => {
      const data = res.data || [];
      setFiliales(data);
      const filialeUser = user?.roles?.[0]?.filiale?._id || user?.roles?.[0]?.filiale?.id || user?.filiale_id || data[0]?._id || data[0]?.id;
      if (filialeUser) setForm(f => ({ ...f, filiale: filialeUser }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- effet volontairement limite au montage
  }, []);

  const ajouterFichiers = (files) => {
    const nouveaux = Array.from(files).slice(0, 5 - fichiers.length).map((f, i) => ({
      file: f,
      id: Date.now() + i,
      categorie: fichiers.length === 0 && i === 0 ? 'document_principal' : 'autre',
      preview: null,
    }));
    setFichiers(prev => [...prev, ...nouveaux]);
    if (!form.titre && nouveaux[0]) {
      const nomSansExt = nouveaux[0].file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setForm(f => ({ ...f, titre: nomSansExt }));
    }
    // Aperçu du premier fichier PDF
    if (nouveaux[0]?.file.type === 'application/pdf') {
      chargerPreviewPDF(nouveaux[0].file);
    }
  };

  const chargerPreviewPDF = async (file) => {
    setPdfLoading(true);
    const url = URL.createObjectURL(file);
    setPdfPreview(url);
    setPdfLoading(false);
  };

  const supprimerFichier = (id) => {
    setFichiers(prev => prev.filter(f => f.id !== id));
    if (fichiers.length <= 1) setPdfPreview(null);
  };

  const changerCategorie = (id, categorie) => {
    setFichiers(prev => prev.map(f => f.id === id ? { ...f, categorie } : f));
  };

  const voirFichier = async (fichier) => {
    if (fichier.file.type === 'application/pdf') {
      chargerPreviewPDF(fichier.file);
    }
  };

  const soumettre = async () => {
    // Fichier optionnel - pas de validation obligatoire
    if (!form.titre.trim()) { setError('Le titre est obligatoire'); return; }
    // filiale prise automatiquement depuis user.filiale_id
    setSaving(true); setError('');
    try {
      const formData = new FormData();
      formData.append('type_document', typeSelectionne.key);
      formData.append('type', typeSelectionne.key);
      formData.append('titre', form.titre);
      formData.append('motif', form.titre);
      formData.append('montant', form.montant || '0');
      formData.append('montantEstime', form.montant || '0');
      formData.append('filiale_id', form.filiale);
      formData.append('ville', form.ville || '');
      formData.append('filiale', form.filiale);
      formData.append('description', form.description || '');
      formData.append('priorite', form.priorite);
      formData.append('urgence', form.priorite === 'P1');
      fichiers.forEach((f, i) => {
        formData.append('documents', f.file);
      });
      await api.post('/demandes', formData);
      allerDashboard();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de la creation');
    } finally { setSaving(false); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' Ko';
    return (bytes / 1048576).toFixed(1) + ' Mo';
  };

  const filiale = filiales.find(f => (f._id || f.id) === form.filiale);

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div style={s.header}>
        <button onClick={allerDashboard} style={s.backBtn}>
          ← Dashboard
        </button>
        <div style={{ flex: 1 }}>
          <div style={s.pageTitle}>Nouvelle demande</div>
          <div style={s.pageSub}>Etape {step + 1} sur 2</div>
        </div>
        {form.priorite === 'P1' && <span style={s.urgentBadge}><IconeFeu taille={11} /> Urgent P1</span>}
      </div>

      {/* BARRE PROGRESSION */}
      <div style={s.progressBar}>
        <div style={{ ...s.progressFill, width: (step + 1) * 50 + '%' }} />
      </div>

      {/* STEPPER */}
      <div style={s.stepperWrap}>
        {['Type & Document', 'Informations'].map((label, i) => (
          <React.Fragment key={i}>
            <div style={s.stepItem}>
              <div style={{ ...s.stepCircle, background: i < step ? '#27AE60' : i === step ? '#C0392B' : '#E5E7EB', color: i <= step ? '#fff' : '#aaa', border: i === step ? '2px solid #C0392B' : i < step ? '2px solid #27AE60' : '2px solid #E5E7EB' }}>
                {i < step ? <IconeValide taille={14} /> : i + 1}
              </div>
              <span style={{ fontSize: 12, color: i === step ? '#C0392B' : i < step ? '#27AE60' : '#aaa', fontWeight: i === step ? 700 : 400 }}>{label}</span>
            </div>
            {i < 1 && <div style={{ ...s.stepLine, background: i < step ? '#27AE60' : '#E5E7EB' }} />}
          </React.Fragment>
        ))}
      </div>

      <div style={estMobile ? { ...s.content, ...s.contentMobile } : s.content}>
        {/* ===== ÉTAPE 0 — TYPE + UPLOAD ===== */}
        {step === 0 && (
          <div style={estMobile ? s.splitLayoutMobile : s.splitLayout}>
            {/* Gauche — Type + Upload */}
            <div style={s.leftPanel}>
              {/* Choix type */}
              {!typeSelectionne ? (
                <div style={s.card}>
                  <div style={s.cardTitle}>Quel type de document ?</div>
                  <div className="grille-mobile-2" style={s.typesGrid}>
                    {TYPES_DOCS.map(type => (
                      <div key={type.key} onClick={() => setTypeSelectionne(type)}
                        style={s.typeCard}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: type.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 10 }}>
                          <type.icon taille={26} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A', marginBottom: 4 }}>{type.label}</div>
                        <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4, marginBottom: 8 }}>{type.desc}</div>
                        <div style={{ fontSize: 10, color: type.color, background: type.bg, padding: '3px 8px', borderRadius: 10, fontWeight: 600 }}>
                          {circuitsCharges[type.key] || 'Chargement du circuit...'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  {/* Type sélectionné */}
                  <div style={{ ...s.card, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: typeSelectionne.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                          <typeSelectionne.icon taille={26} />
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A' }}>{typeSelectionne.label}</div>
                          <div style={{ fontSize: 11, color: typeSelectionne.color }}>{typeSelectionne.circuit}</div>
                        </div>
                      </div>
                      <button onClick={() => { setTypeSelectionne(null); setFichiers([]); setPdfPreview(null); }}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>
                        Changer
                      </button>
                    </div>
                  </div>

                  {/* Zone upload */}
                  <div style={s.card}>
                    <div style={s.cardTitle}>
                      Documents ({fichiers.length}/5)
                      <span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginLeft: 8 }}>
                        Le premier = document principal
                      </span>
                    </div>

                    {/* Drop zone */}
                    <div
                      onDrop={e => { e.preventDefault(); setDragOver(false); ajouterFichiers(e.dataTransfer.files); }}
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onClick={() => fichiers.length < 5 && fileInputRef.current?.click()}
                      style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}), opacity: fichiers.length >= 5 ? 0.5 : 1, cursor: fichiers.length >= 5 ? 'not-allowed' : 'pointer' }}>
                      <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                        style={{ display: 'none' }}
                        onChange={e => ajouterFichiers(e.target.files)} />
                      <div style={{ marginBottom: 10, color: '#ccc' }}><IconeDossier taille={36} /></div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A', marginBottom: 4 }}>
                        {fichiers.length >= 5 ? 'Maximum atteint (5 fichiers)' : 'Glissez vos fichiers ici'}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>ou cliquez pour parcourir</div>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {typeSelectionne.extensions.map(ext => (
                          <span key={ext} style={{ fontSize: 10, background: '#F0F0F0', color: '#666', padding: '2px 8px', borderRadius: 10 }}>.{ext}</span>
                        ))}
                      </div>
                    </div>

                    {/* Liste fichiers */}
                    {fichiers.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {fichiers.map((f, i) => (
                          <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: i === 0 ? '#EAF4FB' : '#F9FAFB', borderRadius: 10, border: i === 0 ? '1px solid #2980B9' : '0.5px solid #e5e7eb' }}>
                            <span style={{ fontSize: 20, flexShrink: 0 }}>
                              {f.file.type.includes('pdf') ? <IconeDocument taille={16} /> : f.file.type.includes('image') ? <IconeImage taille={16} /> : <IconeTrombone taille={16} />}
                            </span>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {i === 0 && <span style={{ fontSize: 9, background: '#2980B9', color: '#fff', padding: '1px 6px', borderRadius: 8, marginRight: 6, fontWeight: 700 }}>PRINCIPAL</span>}
                                {f.file.name}
                              </div>
                              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{formatSize(f.file.size)}</div>
                            </div>
                            <select value={f.categorie} onChange={e => changerCategorie(f.id, e.target.value)}
                              style={{ padding: '4px 8px', borderRadius: 6, border: '0.5px solid #ddd', fontSize: 11, background: '#fff', fontFamily: 'inherit' }}>
                              {CATEGORIES_FICHIER.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                            {f.file.type.includes('pdf') && (
                              <button onClick={() => voirFichier(f)}
                                style={{ padding: '4px 8px', borderRadius: 6, border: '0.5px solid #2980B9', background: '#EAF4FB', color: '#2980B9', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                                <IconeOeil taille={14} />
                              </button>
                            )}
                            <button onClick={() => supprimerFichier(f.id)}
                              style={{ background: 'transparent', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>
                              <IconeFermer taille={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bouton suivant */}
                  {fichiers.length > 0 && (
                    <button onClick={() => setStep(1)} style={s.btnSuivant}>
                      Remplir les informations →
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Droite — Preview PDF */}
            <div style={estMobile ? s.rightPanelMobile : s.rightPanel}>
              <div style={s.previewBox}>
                {pdfLoading ? (
                  <div style={s.previewEmpty}>
                    <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>
                    Chargement...
                  </div>
                ) : pdfPreview ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ background: '#0D1B2A', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#8899AA' }}><IconeDocument taille={12} /> Aperçu du document</span>
                      <button onClick={() => setPdfPreview(null)}
                        style={{ background: 'transparent', border: 'none', color: '#8899AA', cursor: 'pointer', fontSize: 14 }}><IconeFermer taille={14} /></button>
                    </div>
                    <iframe src={pdfPreview} style={{ flex: 1, border: 'none' }} title="Apercu" />
                  </div>
                ) : (
                  <div style={s.previewEmpty}>
                    <div style={{ marginBottom: 14, opacity: 0.3 }}><IconeDocument taille={56} /></div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1B2A', marginBottom: 6 }}>
                      Aperçu du document
                    </div>
                    <div style={{ fontSize: 12, color: '#888', textAlign: 'center', lineHeight: 1.6 }}>
                      Uploadez un PDF pour le visualiser ici avant de soumettre
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== ÉTAPE 1 — INFORMATIONS ===== */}
        {step === 1 && (
          <div style={estMobile ? s.splitLayoutMobile : s.splitLayout}>
            {/* Gauche — Formulaire */}
            <div style={s.leftPanel}>
              <div style={s.card}>
                <div style={s.cardTitle}>Informations de la demande</div>

                {/* Titre */}
                <div style={s.formGroup}>
                  <label style={s.label}>Titre de la demande <span style={{ color: '#C0392B' }}>*</span></label>
                  <input style={{ ...s.input, border: form.titre ? '1.5px solid #27AE60' : '1.5px solid #E5E7EB' }}
                    placeholder="Ex: Achat materiel chantier Nord"
                    value={form.titre}
                    onChange={e => setForm({ ...form, titre: e.target.value })} />
                </div>

                {/* Montant */}
                <div style={s.formGroup}>
                  <label style={s.label}>Montant estimé (FCFA)</label>
                  <input style={s.input} type="number" placeholder="0"
                    value={form.montant}
                    onChange={e => setForm({ ...form, montant: e.target.value })} />
                  {form.montant && (
                    <div style={{ fontSize: 12, color: '#27AE60', marginTop: 4, fontWeight: 600 }}>
                      = {Number(form.montant).toLocaleString('fr-FR')} FCFA
                    </div>
                  )}
                  {/* Indication seulement : c'est le serveur qui tranche au visa
                      de l'assistante, sur le seuil en vigueur a ce moment-la. */}
                  {(() => {
                    const info = messageSeuil(form.montant, seuilDeFiliale(filiales, form.filiale));
                    if (!info) return null;
                    const bleu = info.ton === 'info';
                    return (
                      <div style={{
                        marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 12,
                        background: bleu ? '#EAF4FB' : '#F2F3F4',
                        border: `1px solid ${bleu ? '#1A5E8A' : '#ddd'}`,
                        color: bleu ? '#1A5E8A' : '#555',
                      }}>
                        <strong>{info.texte}</strong>
                        <div style={{ marginTop: 2, opacity: 0.85 }}>{info.detail}</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Filiale masquee - prise automatiquement depuis le compte utilisateur */}

                {/* Ville */}
                <div style={s.formGroup}>
                  <label style={s.label}>Ville <span style={{ color: '#C0392B' }}>*</span></label>
                  <select style={s.input} value={form.ville}
                    onChange={e => setForm({ ...form, ville: e.target.value })}>
                    <option value="">Sélectionnez une ville</option>
                    <optgroup label="Estuaire">
                      <option value="Libreville">Libreville</option>
                      <option value="Owendo">Owendo</option>
                      <option value="Ntoum">Ntoum</option>
                      <option value="Cocobeach">Cocobeach</option>
                    </optgroup>
                    <optgroup label="Haut-Ogooué">
                      <option value="Franceville">Franceville</option>
                      <option value="Moanda">Moanda</option>
                      <option value="Masuku">Masuku</option>
                      <option value="Okondja">Okondja</option>
                      <option value="Lekoni">Lekoni</option>
                    </optgroup>
                    <optgroup label="Moyen-Ogooué">
                      <option value="Lambaréné">Lambaréné</option>
                      <option value="Ndjolé">Ndjolé</option>
                    </optgroup>
                    <optgroup label="Ngounié">
                      <option value="Mouila">Mouila</option>
                      <option value="Fougamou">Fougamou</option>
                      <option value="Ndendé">Ndendé</option>
                      <option value="Mbigou">Mbigou</option>
                    </optgroup>
                    <optgroup label="Nyanga">
                      <option value="Tchibanga">Tchibanga</option>
                      <option value="Mayumba">Mayumba</option>
                      <option value="Moabi">Moabi</option>
                    </optgroup>
                    <optgroup label="Ogooué-Ivindo">
                      <option value="Makokou">Makokou</option>
                      <option value="Booué">Booué</option>
                      <option value="Mékambo">Mékambo</option>
                    </optgroup>
                    <optgroup label="Ogooué-Lolo">
                      <option value="Koulamoutou">Koulamoutou</option>
                      <option value="Lastoursville">Lastoursville</option>
                    </optgroup>
                    <optgroup label="Ogooué-Maritime">
                      <option value="Port-Gentil">Port-Gentil</option>
                      <option value="Omboué">Omboué</option>
                      <option value="Gamba">Gamba</option>
                    </optgroup>
                    <optgroup label="Woleu-Ntem">
                      <option value="Oyem">Oyem</option>
                      <option value="Bitam">Bitam</option>
                      <option value="Mitzic">Mitzic</option>
                      <option value="Minvoul">Minvoul</option>
                    </optgroup>
                  </select>
                </div>

                {/* Priorité */}
                <div style={s.formGroup}>
                  <label style={s.label}>Priorité</label>
                  <div className="grille-mobile-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                    {[
                      { key: 'P1', label: 'P1 — Urgent', icon: IconeFeu, color: '#C0392B', bg: '#FDEDEC' },
                      { key: 'P2', label: 'P2 — Normal', icon: IconeEpingle, color: '#9A7D0A', bg: '#FEF9E7' },
                      { key: 'P3', label: 'P3 — Faible', icon: IconeHorloge, color: '#2980B9', bg: '#EAF4FB' },
                    ].map(p => (
                      <div key={p.key} onClick={() => setForm({ ...form, priorite: p.key })}
                        style={{ padding: '12px 8px', borderRadius: 10, border: form.priorite === p.key ? `2px solid ${p.color}` : '2px solid #E5E7EB', background: form.priorite === p.key ? p.bg : '#FAFAFA', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ marginBottom: 4, color: p.color }}><p.icon taille={20} /></div>
                        <div style={{ fontSize: 11, fontWeight: form.priorite === p.key ? 700 : 400, color: form.priorite === p.key ? p.color : '#555' }}>{p.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div style={s.formGroup}>
                  <label style={s.label}>Description (optionnel)</label>
                  <textarea style={{ ...s.input, height: 80, resize: 'vertical' }}
                    placeholder="Contexte, informations complementaires..."
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })} />
                </div>

                  {/* Zone upload */}
                  <div style={s.card}>
                    <div style={s.cardTitle}>
                      Documents ({fichiers.length}/5)
                      <span style={{ fontSize: 11, color: '#888', fontWeight: 400, marginLeft: 8 }}>
                        Le premier = document principal
                      </span>
                    </div>

                    {/* Drop zone */}
                    <div
                      onDrop={e => { e.preventDefault(); setDragOver(false); ajouterFichiers(e.dataTransfer.files); }}
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onClick={() => fichiers.length < 5 && fileInputRef.current?.click()}
                      style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}), opacity: fichiers.length >= 5 ? 0.5 : 1, cursor: fichiers.length >= 5 ? 'not-allowed' : 'pointer' }}>
                      <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                        style={{ display: 'none' }}
                        onChange={e => ajouterFichiers(e.target.files)} />
                      <div style={{ marginBottom: 10, color: '#ccc' }}><IconeDossier taille={36} /></div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A', marginBottom: 4 }}>
                        {fichiers.length >= 5 ? 'Maximum atteint (5 fichiers)' : 'Glissez vos fichiers ici'}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>ou cliquez pour parcourir</div>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {(typeSelectionne?.extensions || ['pdf']).map(ext => (
                          <span key={ext} style={{ fontSize: 10, background: '#F0F0F0', color: '#666', padding: '2px 8px', borderRadius: 10 }}>.{ext}</span>
                        ))}
                      </div>
                    </div>

                    {/* Liste fichiers */}
                    {fichiers.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {fichiers.map((f, i) => (
                          <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: i === 0 ? '#EAF4FB' : '#F9FAFB', borderRadius: 10, border: i === 0 ? '1px solid #2980B9' : '0.5px solid #e5e7eb' }}>
                            <span style={{ fontSize: 20, flexShrink: 0 }}>
                              {f.file.type.includes('pdf') ? <IconeDocument taille={16} /> : f.file.type.includes('image') ? <IconeImage taille={16} /> : <IconeTrombone taille={16} />}
                            </span>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {i === 0 && <span style={{ fontSize: 9, background: '#2980B9', color: '#fff', padding: '1px 6px', borderRadius: 8, marginRight: 6, fontWeight: 700 }}>PRINCIPAL</span>}
                                {f.file.name}
                              </div>
                              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{formatSize(f.file.size)}</div>
                            </div>
                            <select value={f.categorie} onChange={e => changerCategorie(f.id, e.target.value)}
                              style={{ padding: '4px 8px', borderRadius: 6, border: '0.5px solid #ddd', fontSize: 11, background: '#fff', fontFamily: 'inherit' }}>
                              {CATEGORIES_FICHIER.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                            {f.file.type.includes('pdf') && (
                              <button onClick={() => voirFichier(f)}
                                style={{ padding: '4px 8px', borderRadius: 6, border: '0.5px solid #2980B9', background: '#EAF4FB', color: '#2980B9', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                                <IconeOeil taille={14} />
                              </button>
                            )}
                            <button onClick={() => supprimerFichier(f.id)}
                              style={{ background: 'transparent', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>
                              <IconeFermer taille={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>


                {error && <div style={s.errorMsg}><IconeAlerte taille={13} /> {error}</div>}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={allerDashboard} style={s.btnPrev}>← Retour au Dashboard</button>
                  <button onClick={soumettre} disabled={saving} style={s.btnSoumettre}>
                    {saving ? <><IconeHorloge taille={14} /> Envoi...</> : <><IconeValide taille={14} /> Soumettre le document</>}
                  </button>
                </div>
              </div>
            </div>

            {/* Droite — Récap + Preview */}
            <div style={estMobile ? s.rightPanelMobile : s.rightPanel}>
              {/* Récap type + fichiers */}
              <div style={{ ...s.card, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, fontWeight: 700 }}>Récapitulatif</div>

                {/* Type */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '10px 12px', background: typeSelectionne?.bg, borderRadius: 10 }}>
                  <span style={{ display: 'inline-flex' }}>{typeSelectionne && <typeSelectionne.icon taille={24} />}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>{typeSelectionne?.label}</div>
                    <div style={{ fontSize: 10, color: typeSelectionne?.color, fontWeight: 600 }}>{typeSelectionne?.circuit}</div>
                  </div>
                </div>

                {/* Infos */}
                {[
                  { label: 'Filiale', value: filiale ? filiale.code + ' — ' + filiale.nom : '—' },
                  { label: 'Montant', value: form.montant ? Number(form.montant).toLocaleString('fr-FR') + ' FCFA' : '—', red: true },
                  { label: 'Priorite', value: form.priorite === 'P1' ? <><IconeFeu taille={12} /> P1 Urgent</> : form.priorite === 'P3' ? <><IconeHorloge taille={12} /> P3 Faible</> : <><IconeEpingle taille={12} /> P2 Normal</> },
                  { label: 'Fichiers', value: fichiers.length + ' document(s)' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid #f0f0f0', fontSize: 12 }}>
                    <span style={{ color: '#888' }}>{item.label}</span>
                    <span style={{ fontWeight: 600, color: item.red ? '#C0392B' : '#0D1B2A' }}>{item.value}</span>
                  </div>
                ))}

                {/* Fichiers liste */}
                <div style={{ marginTop: 10 }}>
                  {fichiers.map((f, i) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 11, color: '#555' }}>
                      <span style={{ display: 'inline-flex' }}>{f.file.type.includes('pdf') ? <IconeDocument taille={14} /> : <IconeTrombone taille={14} />}</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                      <span style={{ color: '#aaa', flexShrink: 0 }}>{formatSize(f.file.size)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview PDF */}
              {pdfPreview && (
                <div style={{ ...s.previewBox, height: 400 }}>
                  <div style={{ background: '#0D1B2A', padding: '8px 14px' }}>
                    <span style={{ fontSize: 12, color: '#8899AA' }}><IconeDocument taille={12} /> Document principal</span>
                  </div>
                  <iframe src={pdfPreview} style={{ flex: 1, border: 'none', height: '360px' }} title="Apercu" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F5F6FA', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 },
  backBtn: { background: 'transparent', border: '0.5px solid #ddd', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', flexShrink: 0 },
  pageTitle: { fontSize: 18, fontWeight: 700, color: '#0D1B2A' },
  pageSub: { fontSize: 12, color: '#888', marginTop: 2 },
  urgentBadge: { background: '#FDEDEC', color: '#96281B', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 },
  progressBar: { height: 4, background: '#F0F0F0' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #C0392B, #E74C3C)', transition: 'width 0.4s ease' },
  stepperWrap: { display: 'flex', alignItems: 'center', padding: '16px 24px', background: '#fff', borderBottom: '0.5px solid #e5e7eb' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  stepCircle: { width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  stepLine: { flex: 1, height: 2, margin: '0 12px', minWidth: 60 },
  content: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  contentMobile: { padding: '14px', margin: 0 },
  splitLayout: { display: 'grid', gridTemplateColumns: '1fr 420px', gap: 20, alignItems: 'flex-start' },
  splitLayoutMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 14, alignItems: 'flex-start' },
  leftPanel: { display: 'flex', flexDirection: 'column', gap: 14 },
  rightPanel: { position: 'sticky', top: 24 },
  // Empilee sur mobile : un panneau collant masquerait le formulaire
  rightPanelMobile: { position: 'static' },
  card: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 18, letterSpacing: '-0.2px' },
  typesGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  typeCard: { border: '1.5px solid #E5E7EB', borderRadius: 12, padding: 16, cursor: 'pointer', background: '#FAFAFA', transition: '0.15s' },
  dropZone: { border: '2px dashed #E5E7EB', borderRadius: 12, padding: '32px 20px', minHeight: 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', cursor: 'pointer', background: '#FAFAFA', transition: '0.15s', marginBottom: 14 },
  dropZoneActive: { border: '2px dashed #C0392B', background: '#FDEDEC' },
  previewBox: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', overflow: 'hidden', height: 500, display: 'flex', flexDirection: 'column', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  previewEmpty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, color: '#888', textAlign: 'center' },
  formGroup: { marginBottom: 18 },
  label: { fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#FAFAFA', boxSizing: 'border-box', color: '#0D1B2A' },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 16 },
  btnSuivant: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #C0392B, #96281B)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(192,57,43,0.3)', marginTop: 8 },
  btnPrev: { padding: '12px 20px', borderRadius: 10, border: '1.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  btnSoumettre: { flex: 1, padding: '13px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #27AE60, #1E8449)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(39,174,96,0.3)' },
};
